import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import Pointer from './Pointer';
import { clamp } from './utils/clamp';
import { useInertiaDrag } from './hooks/useInertiaDrag';
import { useOffsetCalc } from './hooks/useOffsetCalc';
import { useResizeObserver } from './hooks/useResizeObserver';
import { useValue } from './hooks/useValue';
const TAP_MAX_MOVEMENT_PX = 5;
export default function Slider({ scale, value, min, max, ratio = 1, retention = 0.95, velocityThreshold = 0.05, onChange, onResize, }) {
    const [offset, setOffset] = useState(0); // offset of the background (px)
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    // Container-local X of the press, and whether it travelled far enough to be a drag.
    const pointerDownXRef = useRef(0);
    const pointerMovedRef = useRef(false);
    useResizeObserver({ ref: containerRef, onResize });
    // Calculate DOM boundaries and possible offsets.
    const { minScaleOffset, minUserOffset, maxUserOffset, calcOffsetWithRatio } = useOffsetCalc({
        contentRef,
        containerRef,
        ratio,
        min,
        max,
    });
    // Managing dragging and animation.
    const { startDrag, moveDrag, endDrag, animateTo, movingLasts, isAnimatingExternally } = useInertiaDrag({
        minOffset: minUserOffset,
        maxOffset: maxUserOffset,
        velocityThreshold,
        retention,
        setOffset,
    });
    // Managing value state (both from inside the component and from the outside).
    useValue({
        offset,
        minScaleOffset,
        ratio,
        min,
        max,
        value,
        movingLasts,
        isAnimatingExternally,
        onChange,
        setOffset,
        animateTo,
        calcOffsetWithRatio,
    });
    const handlePointerDown = (event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        const containerLeft = containerRef.current?.getBoundingClientRect().left ?? 0;
        pointerDownXRef.current = event.clientX - containerLeft;
        pointerMovedRef.current = false;
        startDrag(event.clientX);
    };
    const handlePointerMove = (event) => {
        const containerLeft = containerRef.current?.getBoundingClientRect().left ?? 0;
        if (Math.abs(event.clientX - containerLeft - pointerDownXRef.current) > TAP_MAX_MOVEMENT_PX) {
            pointerMovedRef.current = true;
        }
        // Pointermove on mobile (and busy desktop) coalesces multiple raw moves into one
        // delivered event. getCoalescedEvents() exposes the sub-samples so we can apply
        // each finger increment individually instead of seeing one big delta-jump.
        const coalescedEvents = event.nativeEvent.getCoalescedEvents();
        if (coalescedEvents.length > 0) {
            for (const coalescedEvent of coalescedEvents) {
                moveDrag(coalescedEvent.clientX);
            }
        }
        else {
            moveDrag(event.clientX);
        }
    };
    const handlePointerEnd = () => {
        endDrag();
        // A press that never travelled is a tap: animate the tapped scale point to the
        // centre (where the pointer sits). The scale point under the tap currently sits
        // at `pointerDownX - offset`; to centre it the new offset must satisfy
        // `point + newOffset = width / 2`, i.e. newOffset = offset + (width / 2 - tapX).
        if (!pointerMovedRef.current && containerRef.current) {
            const containerWidth = containerRef.current.clientWidth;
            const targetOffset = clamp(offset + containerWidth / 2 - pointerDownXRef.current, maxUserOffset, minUserOffset);
            animateTo(offset, targetOffset, false);
        }
    };
    return (_jsxs("div", { className: "relative h-full touch-none overflow-hidden select-none", ref: containerRef, children: [_jsx("div", { className: "absolute top-0 left-0 h-full", style: { transform: `translateX(${offset}px)` }, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerEnd, onPointerCancel: handlePointerEnd, children: _jsx("div", { className: "h-full", ref: contentRef, children: scale }) }), _jsx(Pointer, {})] }));
}
